import {
  FilterItemCountsWithSelect,
  FilterOption,
  MetadataBarTypes,
  MetadataSource,
  TrackingFilterTypes,
} from "types";
import { trackFilterSelection } from "helpers/utility/amplitude";

import MultipleSelectionsContent from "./MultipleSelectionsContent";
import FileDisplayContent from "./FileDisplayContent";
import BarDisplayContent from "./BarDisplayContent";
import CaptionContent from "./CaptionContent";

import styles from "./style.module.scss";

function trackMetadataFilterChange(
  filterType: TrackingFilterTypes,
  entityState: FilterOption[],
  changeType: "Added" | "Removed",
) {
  trackFilterSelection(filterType, "Metadata Summary", entityState, changeType);
}

interface ContentBodyProps {
  title: string;
  type: MetadataBarTypes;
  data: Array<FilterItemCountsWithSelect>;
  showNoMatchingData: boolean;
  isContentSelected: boolean;
  entityTypeFilter: FilterOption[];
  sources?: MetadataSource[];
  showCount: boolean;
  isLoading: boolean;
  searchDisabled: boolean;
  getIsUnselectable?: (value: FilterItemCountsWithSelect) => boolean;
  useAccordionHeight?: boolean;
}

const ContentBody = ({
  title,
  type,
  data,
  sources,
  showNoMatchingData,
  isContentSelected,
  entityTypeFilter,
  showCount,
  isLoading,
  searchDisabled,
  getIsUnselectable,
  useAccordionHeight = true,
}: ContentBodyProps) => {
  const sortedData = ["labels", "issue-types"].includes(type)
    ? data
    : data.slice().sort((a: any, b: any) => b.count - a.count);

  const renderContentForType = (type: MetadataBarTypes) => {
    if (data.length === 0) {
      return (
        <div className={styles.noMatchcontainer}>
          Currently no matching {title.toLowerCase()}
        </div>
      );
    }
    switch (type) {
      case "file-display":
        return (
          <FileDisplayContent
            data={sortedData}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            showNoMatchingData={showNoMatchingData}
            isContentSelected={isContentSelected}
          />
        );
      case "labels":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );
      case "user-tags":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );
      case "issue-types":
        return (
          <BarDisplayContent
            type={type}
            data={sortedData}
            showCount={showCount}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );

      case "sources":
      case "models":
        return (
          <MultipleSelectionsContent
            data={sortedData}
            isLoading={isLoading}
            searchDisabled={searchDisabled}
            entityTypeFilter={entityTypeFilter}
            getIsUnselectable={getIsUnselectable}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
            trackMetadataFilterChange={trackMetadataFilterChange}
            contentType={type}
            sources={sources!}
            useAccordionHeight={useAccordionHeight}
          />
        );

      case "captions":
        return (
          <CaptionContent
            data={sortedData}
            isContentSelected={isContentSelected}
            showNoMatchingData={showNoMatchingData}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={styles.contentBody}
      style={isContentSelected ? { height: "calc(100% - 40px)" } : undefined}
    >
      {renderContentForType(type)}
    </div>
  );
};

export default ContentBody;
